import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BrandLogo } from '../../shared/components/BrandLogo';
import { Icon } from '../../shared/components/Icon';
import { LanguageSelect } from '../../shared/components/LanguageSelect';
import { GoogleSignInButton } from '../../shared/components/GoogleSignInButton';
import { PoweredBy } from '../../shared/components/ui';
import { Tooltip } from '../../shared/components/Tooltip';
import { RecaptchaNotice } from '../../shared/security/RecaptchaNotice';
import { useTheme } from '../../shared/theme/theme';
import { ApiRequestError } from '../../shared/api/http';
import { authApi, type ExternalProviders, type MfaMethod } from '../../shared/api/auth';
import { useOffline } from '../../offline/OfflineProvider';
import { MAX_OFFLINE_DAYS } from '../../offline/vault';
import { useAuth } from './AuthContext';

function authenticationErrorKey(error: unknown): string {
  if (!(error instanceof ApiRequestError)) return 'auth.connectionError';
  if (error.code === 'authentication.temporarily_locked') return 'auth.lockedError';
  // A captcha refusal is not a credentials refusal, and saying so is what tells someone whose browser blocks
  // Google that their password is not the problem.
  if (error.code === 'security.captcha_required' || error.code === 'security.captcha_rejected') return 'auth.captchaError';
  if (error.code === 'security.captcha_unavailable') return 'auth.captchaUnavailableError';
  if (error.code === 'authentication.invalid_credentials' || error.status === 401) return 'auth.credentialsError';
  return 'auth.connectionError';
}

/**
 * How the sign-in card is dressed, for the shell that hosts it.
 *
 * There is one sign-in page in this product and there will go on being one — a second would be
 * a second set of security decisions to get wrong. What a shell may vary is presentation, so
 * everything here is presentation, and every default is the classic console's own behaviour.
 */
export interface LoginPageProps {
  /**
   * Whether the card introduces the product above the form: the brand tagline and the sentence
   * explaining what signing in leads to. A shell that has already introduced itself — one that
   * paints the branded workspace behind this card — says it twice by leaving these on.
   */
  readonly showIntroduction?: boolean;

  /**
   * Where the explanation of the encrypted device copy is put. `inline` prints it under the
   * option, which is the right thing on a page a first-time operator reads top to bottom;
   * `tooltip` moves it behind a mark, which is the right thing once the form is furniture.
   */
  readonly offlineHint?: 'inline' | 'tooltip';

  /**
   * Whether the Google attribution is printed under the form. Google requires it from any site
   * that hides the reCAPTCHA badge, so it must stay on wherever the browser mints tokens — and
   * must come off wherever it does not, because a notice about a service the page never calls
   * is simply untrue.
   */
  readonly showRecaptchaNotice?: boolean;
}

export function LoginPage({
  showIntroduction = true,
  offlineHint = 'inline',
  showRecaptchaNotice = true,
}: LoginPageProps = {}) {
  const { t } = useTranslation();
  const { login, completeMfa } = useAuth();
  const [email, setEmail] = useState('admin');
  const [password, setPassword] = useState('admin');
  const [totpCode, setTotpCode] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaMethods, setMfaMethods] = useState<readonly MfaMethod[]>([]);
  const [selectedMethodId, setSelectedMethodId] = useState('');
  const [providers, setProviders] = useState<ExternalProviders>({ google: false, microsoft: false });
  const [developmentCode, setDevelopmentCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [rememberOffline, setRememberOffline] = useState(false);
  const offline = useOffline();
  const { theme, toggle: toggleTheme } = useTheme();
  const selectedMethod = mfaMethods.find((method) => method.id === selectedMethodId) ?? null;

  useEffect(() => {
    void authApi.providers().then(setProviders).catch(() => undefined);
    const query = new URLSearchParams(window.location.search);
    const externalError = query.get('authError');
    if (externalError) setErrorKey(`auth.${externalError}`);
    if (query.get('mfa') === '1') {
      void authApi.mfaChallenge()
        .then((methods) => {
          setMfaMethods(methods);
          setSelectedMethodId(methods[0]?.id ?? '');
          setMfaRequired(true);
        })
        .catch((error) => setErrorKey(authenticationErrorKey(error)));
    }
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorKey(null);
    try {
      if (mfaRequired) {
        await completeMfa(selectedMethodId, totpCode.trim());
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      const outcome = await login({ email: email.trim(), password }, { rememberOffline });
      if (outcome.status === 'mfa_required') {
        setMfaMethods(outcome.methods);
        setSelectedMethodId(outcome.methods[0]?.id ?? '');
        setMfaRequired(true);
        setErrorKey('auth.mfaPrompt');
      }
    } catch (error) {
      setErrorKey(authenticationErrorKey(error));
    } finally {
      setSubmitting(false);
    }
  };

  const sendCode = async () => {
    if (!selectedMethod || selectedMethod.type === 'totp') return;
    setSubmitting(true);
    setErrorKey(null);
    try {
      const result = await authApi.sendMfaCode(selectedMethod.id);
      setDevelopmentCode(result.developmentCode ?? '');
      setErrorKey('auth.codeSent');
    } catch (error) {
      setErrorKey(authenticationErrorKey(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-page">
      <section className="auth-card" aria-labelledby="auth-title">
        <header className="auth-card__header">
          <Link className="brand__mark" to="/" aria-label={t('app.name')}><BrandLogo /></Link>
          <div>
            {showIntroduction && <p className="brand-tagline">{t('app.tagline')}</p>}
            <h1 id="auth-title">{mfaRequired ? t('auth.verifyTitle') : t('auth.loginTitle')}</h1>
            {/*
              The verification step's sentence stays whatever the shell asked for: it is not an
              introduction but an instruction, and somebody halfway through a challenge needs to
              be told which code to reach for.
            */}
            {(showIntroduction || mfaRequired) && (
              <p>{mfaRequired ? t('auth.verifySubtitle') : t('auth.loginSubtitle')}</p>
            )}
          </div>
          <div className="auth-preferences">
            <LanguageSelect />
            <button
              className="theme-switch"
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            </button>
          </div>
        </header>

        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          {!mfaRequired ? <>
            <label className="field">
              <span>{t('auth.username')}</span>
              <input type="text" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" required disabled={submitting} />
            </label>
            <label className="field">
              <span>{t('auth.password')}</span>
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required disabled={submitting} />
            </label>
            {/*
              Offered here because this is the one moment the password is known to be the right
              one: the server is about to confirm it, and the copy on the device is sealed with
              the very password that worked. It is opt-in, per device, and never the default.
            */}
            {offline.supported && !offline.enabled && (offlineHint === 'tooltip' ? (
              /*
                The mark is a sibling of the label, never a child of it: a button inside a label
                is still inside the label's hit area, so asking what the option means would also
                turn the option on.
              */
              <div className="field--checkbox-row">
                <label>
                  <input
                    type="checkbox"
                    checked={rememberOffline}
                    onChange={(event) => setRememberOffline(event.target.checked)}
                    disabled={submitting}
                  />
                  <strong>{t('offline.login.remember')}</strong>
                </label>
                <Tooltip
                  label={t('offline.login.remember')}
                  text={t('offline.login.rememberHint', { days: MAX_OFFLINE_DAYS })}
                />
              </div>
            ) : (
              <label className="field field--checkbox">
                <input
                  type="checkbox"
                  checked={rememberOffline}
                  onChange={(event) => setRememberOffline(event.target.checked)}
                  disabled={submitting}
                />
                <span>
                  <strong>{t('offline.login.remember')}</strong>
                  <small>{t('offline.login.rememberHint', { days: MAX_OFFLINE_DAYS })}</small>
                </span>
              </label>
            ))}
          </> : <>
            <label className="field">
              <span>{t('auth.verificationMethod')}</span>
              <select value={selectedMethodId} onChange={(event) => { setSelectedMethodId(event.target.value); setDevelopmentCode(''); }} required disabled={submitting}>
                {mfaMethods.map((method) => (
                  <option key={method.id} value={method.id}>
                    {method.displayName}{method.maskedDestination ? ` · ${method.maskedDestination}` : ''}
                  </option>
                ))}
              </select>
            </label>
            {selectedMethod?.type !== 'totp' && (
              <button className="button button--secondary" type="button" onClick={() => void sendCode()} disabled={submitting || !selectedMethod}>
                {t('auth.sendCode')}
              </button>
            )}
            <label className="field">
              <span>{t('auth.verificationCode')}</span>
              <input type="text" value={totpCode} onChange={(event) => setTotpCode(event.target.value)} autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]*" required disabled={submitting} aria-describedby="totp-hint" autoFocus />
              <small id="totp-hint">{selectedMethod?.type === 'totp' ? t('auth.authenticatorHint') : t('auth.deliveredCodeHint')}</small>
            </label>
            {developmentCode && <p className="auth-message">{t('auth.developmentCode', { code: developmentCode })}</p>}
          </>}

          {errorKey && <p className={errorKey === 'auth.mfaPrompt' || errorKey === 'auth.codeSent' ? 'auth-message' : 'auth-error'} role="alert">{t(errorKey)}</p>}

          <button className="button button--primary auth-submit" type="submit" disabled={submitting}>
            {submitting ? t('auth.signingIn') : mfaRequired ? t('auth.verify') : t('auth.signIn')}
          </button>
          {!mfaRequired && (
            <div className="auth-account-links">
              <Link className="text-link" to="/forgot-password">{t('auth.forgotLink')}</Link>
              <Link className="text-link" to="/register">{t('auth.createAccount')}</Link>
            </div>
          )}
        </form>

        {!mfaRequired && (providers.google || providers.microsoft) && <div className="external-login">
          <div className="external-login__divider"><span>{t('auth.orContinueWith')}</span></div>
          <div className="external-login__actions">
            {providers.google && <GoogleSignInButton href={authApi.externalUrl('google')} label={t('auth.continueGoogle')} />}
            {providers.microsoft && <a className="button button--secondary" href={authApi.externalUrl('microsoft')}>{t('auth.continueMicrosoft')}</a>}
          </div>
        </div>}

        {showRecaptchaNotice && <RecaptchaNotice />}
      </section>
      <PoweredBy />
    </main>
  );
}
